import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Input } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuestionCategoryTableColumnNames, fetchAllQuestionCategory, createQuestionCategory, updateQuestionCategory, 
    deleteQuestionCategory, activateDeactivateQuestionCategory, uploadQuestionCategoryIcon, fetchTranslation, updateUserPreference, getQuestionTypes} from "../../../slices/thunks";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import AddModal from "../../../Components/Common/Modal/AddModal";
import AddQuestionCategoryForm from "../../../Components/Common/AddQuestionCategoryForm/AddQuestionCategoryForm";


const labels = [
  {
    id: 1,
    labelName: "CategoryNameEn",
    fieldName: "text2-box",
    name: "CategoryNameEn",
    value: "CategoryNameEn",
    required: true,
  },
  {
    id: 2,
    labelName: "CategoryNameAr",
    fieldName: "text-box",
    name: "CategoryNameAr",
    value: "CategoryNameAr",
    required: true,
  },
  {
    id: 3,
    labelName: "CategoryDescriptionEn",
    fieldName: "textarea2-box",
    name: "CategoryDescriptionEn",
    value: "CategoryDescriptionEn",
    required: true,
  },
  {
    id: 4,
    labelName: "CategoryDescriptionAr",
    fieldName: "textarea-box",
    name: "CategoryDescriptionAr",
    value: "CategoryDescriptionAr",
    required: true,
  },
  {
    id: 5,
    labelName: "CategoryType",
    fieldName: "category-type-dropdown",
    name: "CategoryType",
    value: "CategoryType",
    required: true,
  },
  {
    id: 6,
    labelName: "Icon",
    fieldName: "file",
    name: "Icon",
    value: "Icon",
    required: true,
  }
]


function QuestionCategory () {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const user = getAuthUser();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedCategoryType, setSelectedCategoryType] = useState([]);
  const [lastTranslatedField, setLastTranslatedField] = useState(null);

  const { Icon, QuestionCategory, tableColumnNames, loading, module } = useSelector((state) => ({
    Icon: state.QuestionCategory.icon,
    QuestionCategory: state.QuestionCategory.questionCategory,
    tableColumnNames: state.QuestionCategory.tableColumnNames,
    loading: state.QuestionCategory.loading,
    module: state.Module.module,
  }));

  // Compute permission
  const permission = (() => {
    const moduleItem = module?.find((m) => m.moduleId === 9 && m.modulePermission?.CanView);
    const pageItem = moduleItem?.pages?.find((p) => p.pageId === 8); // Roles Management = pageId: 2
        
      if (moduleItem && pageItem) {
          return {
            CanView:
              moduleItem.modulePermission.CanView && pageItem.permissions.CanView,
            CanAdd:
              moduleItem.modulePermission.CanAdd && pageItem.permissions.CanAdd,
            CanEdit:
              moduleItem.modulePermission.CanEdit && pageItem.permissions.CanEdit,
            CanDelete:
              moduleItem.modulePermission.CanDelete && pageItem.permissions.CanDelete,
            CanActive: moduleItem.modulePermission.CanActive && pageItem.permissions.CanActive,
        };
      }
    
    return {};
  })();

  const handleImageUpload = (files) => {
    const processedFiles = Object.assign(files[0], {
      preview: URL.createObjectURL(files[0]),
      formattedSize: formatBytes(files[0].size),
    })
    setSelectedRow((prev) => ({...prev, ...processedFiles}));
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageDataUrl = reader.result;
      const base64Image = imageDataUrl.split(',')[1];
      dispatch(uploadQuestionCategoryIcon({ ImageData: base64Image, folderName: 'QuestionCategory' }));
    };
    reader.readAsDataURL(processedFiles);
  }
  
  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }
  

  const onAddClickHandler = (value) => {
    setShow(true);
    setIsDisabled(true);
    setIsAddOrEdit(value);
    setSelectedCategoryType([]);
    setSelectedRow({});
  };

  const onEditClickHandler = (questionCategory, value) => {
    setSelectedRow(questionCategory);
    setSelectedCategoryType(questionCategory?.CategoryType || []);
    setIsAddOrEdit(value);
    setIsDisabled(false);
    setShow(true);
  };
  
  const onChangeHandler = (e) => {
    const { name, value } = e.target;

    setSelectedRow((prev) => ({
      ...prev,
      [name]: value
    }));
  };
  
  const onSubmitHandler = (e) => {
    e.preventDefault();

    const questionCategoryObj = {
      CategoryNameEn: selectedRow.CategoryNameEn,
      CategoryNameAr: selectedRow.CategoryNameAr,
      CategoryDescriptionEn: selectedRow.CategoryDescriptionEn,
      CategoryDescriptionAr: selectedRow.CategoryDescriptionAr, 
      CategoryType: selectedCategoryType, 
      Icon: selectedRow.Icon
    };

    if (isAddOrEdit === "isEdit") {
        questionCategoryObj.ID = selectedRow?.ID;
    }

    isAddOrEdit === "isAdd" ?
      dispatch(createQuestionCategory(questionCategoryObj)) :
      dispatch(updateQuestionCategory(questionCategoryObj));

    setShow(false);
  };

  const onTranslateClickHandler = (fieldKey, textToTranslate) => {
    setLastTranslatedField(fieldKey); 
    dispatch(fetchTranslation({ LangText: textToTranslate }));
  };

  const handleSave = async () => {
    const preferencesPayload = {
      UserId: user.UserId,
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ColumnId] = selectedColumns.includes(column.ColumnId);
        return acc;
      }, {}),
    };

    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchAllQuestionCategory({
          module: "QUESTIONCATEGORY"
        })
      );

      setModalOpen(false);
    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteQuestionCategory({ Ids: selectedIds, UserId: user.UserId }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onActiveOrDeactiveChange = (questionCategory, e) => {
    dispatch(activateDeactivateQuestionCategory({ ID: questionCategory.ID, IsActive: questionCategory.IsActive, UserId: user.UserId }));
  };

  useEffect(() => {
    if (Icon) {
      setIsDisabled(false);
      setSelectedRow((prev) => ({
        ...prev,
        Icon: Icon
      }))
    }

  }, [Icon])
  
  useEffect(() => {
    dispatch(fetchQuestionCategoryTableColumnNames({ module: "QUESTIONCATEGORY" }))
    dispatch(fetchAllQuestionCategory());
    dispatch(getQuestionTypes());

  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t('Question Category')} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            {/* <CardHeader>
              <h4 className="card-title mb-0">Add, Edit & Remove</h4>
            </CardHeader> */}

            <CardBody>
              <div className="listjs-table" id="customerList">
                <Row className="g-4 mb-3">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => onAddClickHandler("isAdd")}
                          id="create-btn"
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                  <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                    Loading...
                  </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    <CommonTable
                      data={QuestionCategory}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      hasAction={true}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <AddModal
        show={show}
        setShow={setShow}
        isDisabled={isDisabled}
        onSubmitHandler={onSubmitHandler}
        title={isAddOrEdit === "isAdd" ? t("AddNewQuestionCategory") : t("EditQuestionCategory")}
        modalBody={
          <AddQuestionCategoryForm
            labels={labels}
            isAddOrEdit={isAddOrEdit}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            onChangeHandler={onChangeHandler}
            selectedCategoryType={selectedCategoryType}
            setSelectedCategoryType={setSelectedCategoryType}
            handleImageUpload={handleImageUpload}
            onTranslateClickHandler={onTranslateClickHandler}
            lastTranslatedField={lastTranslatedField}
          />
        }
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={handleSave}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={
          selectedIds.length > 1
            ? `Are you sure you want to delete ${selectedIds.length} selected items?`
            : `Are you sure you want to delete this record?`
        }
      />
    </div>
  );
}

export default QuestionCategory;